/**
 * Online-status context (Phase 3, §5.6). Tracks `navigator.onLine` + the
 * `online`/`offline` events and exposes a `guardOnline()` gate that mutation
 * handlers call before firing a write: offline it raises the "Keine Verbindung"
 * toast and returns `false` so the write is blocked (never queued — financial
 * writes must not silently replay). `OfflineBanner` reads `online`.
 */
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useToast } from './ToastContext.tsx';

export interface OnlineStatus {
  /** True when the browser reports connectivity. */
  online: boolean;
  /** Guard a mutation: returns `true` if online, otherwise raises a "Keine
   * Verbindung" toast and returns `false` so the caller skips the write. */
  guardOnline: () => boolean;
}

const OnlineStatusContext = createContext<OnlineStatus | null>(null);

function readOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine;
}

export function OnlineStatusProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();
  const [online, setOnline] = useState(readOnline);

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  const guardOnline = useCallback((): boolean => {
    // Read the freshest value straight from the platform so a guard fired
    // between an `offline` event and the re-render is still accurate.
    if (readOnline()) return true;
    toast('Keine Verbindung — Änderung nicht möglich', { variant: 'error' });
    return false;
  }, [toast]);

  const value = useMemo<OnlineStatus>(() => ({ online, guardOnline }), [online, guardOnline]);

  return <OnlineStatusContext value={value}>{children}</OnlineStatusContext>;
}

export function useOnlineStatus(): OnlineStatus {
  const ctx = use(OnlineStatusContext);
  if (!ctx) {
    throw new Error('useOnlineStatus muss innerhalb von <OnlineStatusProvider> verwendet werden.');
  }
  return ctx;
}
